import React, { useCallback, useMemo } from 'react';

import { Modal } from '@pkg/components';
import { type TeamWinnerWithLBData, type WinnerWithLBData } from '@pkg/components/Sections/Admin';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAssignCompetitionWinners, useQueryInvalidation } from '@pkg/hooks';

import { type ResourceId, type TeamLeaderboardItem } from '@app/client';
import { FANTASY_WINNERS_CACHE } from '@app/client/resources/competitions';
import { type GameItemData } from '@app/pages/FantasyWishlist/sections/LeaderboardSection/types';

import { Header, PlayerButton, PlayerWrapper } from './styles';
interface WinnerModalProps {
    onClose: () => void;
    teamWinnerItem?: TeamWinnerWithLBData;
    playerWinnerItem?: WinnerWithLBData | TeamWinnerWithLBData;
    leaderboardData?: GameItemData[] | TeamLeaderboardItem[];
}
export const AssignWinnerModal = ({ teamWinnerItem, playerWinnerItem, leaderboardData, onClose }: WinnerModalProps) => {
    const { mutateAsync: updateWinner } = useAssignCompetitionWinners();
    const invalidateWinners = useQueryInvalidation(FANTASY_WINNERS_CACHE);

    const assignWinner = useCallback(
        ({ userId, teamId, winnerItemId }: { userId?: ResourceId; teamId?: ResourceId; winnerItemId?: ResourceId }) => {
            if (winnerItemId) {
                const reason = prompt('Do you have a reason?') || undefined;
                updateWinner({ user_id: userId, team_id: teamId, winnerId: winnerItemId, reason })
                    .then((data) => {
                        if (data.ok) {
                            alert('Successfully updated winner!');
                        }
                        if (!data.ok && data.error) {
                            alert(data.error);
                        }
                        invalidateWinners();
                    })
                    .finally(() => onClose());
            }
        },
        [invalidateWinners, onClose, updateWinner],
    );

    const itemsMarkup = useMemo(
        () =>
            leaderboardData?.map((item) => {
                if (playerWinnerItem && item.user_id) {
                    return (
                        <PlayerButton
                            key={item.user_id}
                            color="transparent"
                            onClick={() => assignWinner({ userId: item.user_id, winnerItemId: playerWinnerItem?.id })}
                            size="xsmall"
                        >
                            <div>
                                {item.username}
                                <span>{item.yat}</span>
                            </div>
                        </PlayerButton>
                    );
                } else {
                    return (
                        <PlayerButton
                            key={item.team_id}
                            color="transparent"
                            onClick={() => assignWinner({ teamId: item.team_id, winnerItemId: teamWinnerItem?.id })}
                            size="xsmall"
                        >
                            <div>{item.team_name}</div>
                        </PlayerButton>
                    );
                }
            }),
        [assignWinner, leaderboardData, playerWinnerItem, teamWinnerItem?.id],
    );

    const prize = playerWinnerItem?.prize || teamWinnerItem?.prize;
    return (
        <Modal padded onClose={onClose}>
            <Modal.Layout>
                <Modal.Layout.Section>
                    <Header>Select a player to assign a new winner for {prize?.name}?</Header>
                    <h3>Reward amount: {prize?.reward}</h3>
                    Current winner:
                    {playerWinnerItem?.user_id ? (
                        <Button
                            color="transparent"
                            size="xsmall"
                            to={`/admin/users/${playerWinnerItem?.user_id}`}
                            target="_blank"
                        >
                            <p>
                                {playerWinnerItem?.username} <span>{playerWinnerItem?.yat}</span>
                            </p>
                        </Button>
                    ) : (
                        teamWinnerItem?.team_name
                    )}
                </Modal.Layout.Section>
                <Modal.Layout.Section>
                    <PlayerWrapper>{itemsMarkup}</PlayerWrapper>
                </Modal.Layout.Section>
            </Modal.Layout>
        </Modal>
    );
};
